/**
 * truckParts — shared parts vocabulary + the note parser, used by Parts for the
 * Day (transition read) AND the plan-accessory "suggest from notes" seed. A
 * filter's full name is its size + cabinet ("20x25 F100" = ONE filter);
 * Aprilaire codes split into humidifier water panels (APR35…) vs air-cleaner
 * filters (APR213…); "HUMI" is just a humidifier-on-plan flag. Dimension- and
 * word-boundary-validated so model #s (F33UX305) and phone numbers don't match.
 */
export type PartKind = "filter" | "aprilaire" | "cabinet" | "humidifier" | "part";
export interface TruckPart { key: string; label: string; kind: PartKind; qty?: number }

const MIN_DIM = 8, MAX_DIM = 32;
const CAB = "F(?:25|35|50|100|150|200|250|300)";
export const HUMIDIFIER_PADS = new Set(["APR10", "APR12", "APR35", "APR45"]);

export function extractTruckParts(notes: string | null): TruckPart[] {
  if (!notes) return [];
  const out = new Map<string, TruckPart>();
  const add = (key: string, label: string, kind: PartKind) => { if (!out.has(key)) out.set(key, { key, label, kind }); };
  // Filter size, optionally followed by its cabinet model → ONE filter ("20x25 F100").
  for (const m of notes.matchAll(/\b(\d{1,2})\s*[xX]\s*(\d{1,2})(?:\s*[xX]\s*(\d))?\b/g)) {
    const w = +m[1], h = +m[2], d = m[3] ? +m[3] : null;
    if (w < MIN_DIM || w > MAX_DIM || h < MIN_DIM || h > MAX_DIM) continue;
    if (d !== null && (d < 1 || d > 6)) continue;
    const size = d ? `${w}x${h}x${d}` : `${w}x${h}`;
    const tail = notes.slice((m.index ?? 0) + m[0].length, (m.index ?? 0) + m[0].length + 8);
    const cab = tail.match(new RegExp(`^\\s+(${CAB})\\b`, "i"));
    const label = cab ? `${size} ${cab[1].toUpperCase()}` : size;
    add(`SZ:${label.toUpperCase()}`, label, "filter");
  }
  // Aprilaire codes — humidifier water panels vs air-cleaner filters.
  let hasPad = false;
  for (const m of notes.matchAll(/\bAPR\s?(\d{2,4})\b/gi)) {
    const code = `APR${m[1]}`;
    const pad = HUMIDIFIER_PADS.has(code);
    add(code, code, pad ? "humidifier" : "aprilaire");
    if (pad) hasPad = true;
  }
  // "HUMI" = humidifier-on-plan flag with no specific pad named → default to the
  // #35 pad (most whole-house humidifiers use it). The plan surface refines this
  // to the exact pad from the unit model when it can (humidifierPad()).
  if (/\bHUMI\w*\b/i.test(notes) && !hasPad) add("APR35", "APR35", "humidifier");
  return [...out.values()];
}

/**
 * Best-guess the humidifier WATER PANEL / canister from the unit MODEL, so a
 * "humidifier on the plan" resolves to the RIGHT pad. Field guide (Seth
 * 2026-06-18): most whole-house humidifiers take the #35 pad; #10 and #12 cover
 * a handful of Aprilaire models; the 800-series steam unit takes the #80
 * canister. Aprilaire model #s + Honeywell HE / Lennox WB·WP equivalents that
 * share the #35 size. Returns the pad code, or null if unrecognized.
 */
export function humidifierPad(model: string | null | undefined): "APR35" | "APR10" | "APR12" | "APR80" | null {
  const s = (model ?? "").toUpperCase();
  if (!s) return null;
  if (/STEAM|CANISTER/.test(s)) return "APR80";
  // Honeywell HE/ME, Lennox WB/WP/HCWB/HCWP, Lennox X2661 → #35
  if (/\bHE\s?\d{2,3}\b|\bME\s?360\b|\bHCW[BP]\b|\bWB\s?\d|\bWP\s?\d|\bX2661\b/.test(s)) return "APR35";
  const num = s.match(/(\d{3})/)?.[1];
  if (num === "800") return "APR80";
  if (num && ["110", "220", "500", "550", "558"].includes(num)) return "APR10";
  if (num && ["112", "136", "224", "440", "445", "448"].includes(num)) return "APR12";
  if (num && ["300", "350", "360", "560", "568", "600", "700", "760", "768"].includes(num)) return "APR35";
  return null;
}

/**
 * PLAN-PARTS-SEED (34i91): is this code something a plan REPLACES at a visit —
 * a water panel, a filter, a media insert, a bulb set? The note parser matches
 * any APR####, so one-time parts slipped into the plan suggestions (0074771:
 * APR4827 is the 5000's door, APR5000 is the cabinet itself). Only consumables
 * belong on a plan: Aprilaire pads + media, Lennox filter stock numbers, filter
 * sizes, and bulbs.
 */
const APR_MEDIA = new Set(["APR201", "APR213", "APR401", "APR410", "APR413", "APR501", "APR510", "APR80"]);
export function isPlanConsumable(p: TruckPart): boolean {
  if (p.kind === "filter" || p.kind === "humidifier") return true;
  const c = p.label.toUpperCase().replace(/\s+/g, "");
  if (HUMIDIFIER_PADS.has(c) || APR_MEDIA.has(c)) return true;
  if (/^L\d{2}-?\d{3}$/.test(c)) return true;
  return /BULB/.test(c);
}
/** The kind a plan part reads as, from its code + pricebook description. */
export function planPartKind(code: string, description?: string | null): PartKind {
  const c = code.toUpperCase().replace(/\s+/g, "");
  const d = (description ?? "").toUpperCase();
  if (HUMIDIFIER_PADS.has(c) || c === "APR80" || /WATER PANEL|PAD\b|CANISTER/.test(d)) return "humidifier";
  if (/^APR\d{3}$/.test(c) || /MEDIA/.test(d)) return "aprilaire";
  if (/^L\d{2}-?\d{3}$/.test(c) || /FILTER/.test(d)) return "filter";
  return "part";
}

export const PART_CHIP: Record<PartKind, string> = {
  filter: "border-sky-200 bg-sky-50 text-sky-700",
  aprilaire: "border-violet-200 bg-violet-50 text-violet-700",
  cabinet: "border-slate-200 bg-slate-100 text-slate-600",
  humidifier: "border-cyan-200 bg-cyan-50 text-cyan-700",
  part: "border-emerald-200 bg-emerald-50 text-emerald-700",
};
export const KIND_ORDER: Record<PartKind, number> = { filter: 0, aprilaire: 1, cabinet: 2, humidifier: 3, part: 4 };
